import React, { useEffect, useRef, useState } from 'react';
import './CartModal.css';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { getAccessToken } from '../services/authApi';
import { getAnalyticsSessionId } from '../services/analyticsService';
import { usePricing } from '../contexts/PricingContext';
import ProfilePromptModal, { wasPromptDismissed } from './ProfilePromptModal';

interface CartItem {
  id: string;
  name: string;
  price: number;
  image?: string;
  /** Número de fotos cuando es Pack Completo */
  photoCount?: number;
}

interface CheckoutItem {
  id: string;
  filename: string;
  school: string;
  date: string;
  price: number;
  thumbnail?: string;
  files?: string[];
}

interface CartModalProps {
  isOpen: boolean;
  onClose: () => void;
  items: CartItem[];
  onRemoveItem: (id: string) => void;
  totalPrice: number;
  /** Se llama justo antes de redirigir a Stripe, para vaciar el carrito y evitar recompras si el cliente vuelve atrás. */
  onCheckoutStart?: () => void;
}

function cartToCheckoutItems(items: CartItem[]): CheckoutItem[] {
  return items.map((item) => {
    let school = (item as any).school ?? '';
    let date = (item as any).date ?? '';
    let filename = ((item as any).filename as string | undefined) || item.name;
    const files = (item as any).files as string[] | undefined;

    if (item.id.startsWith('pack_')) {
      if (!school || !date) {
        const parts = item.id.replace('pack_', '').split('_');
        school = school || parts[0] || '';
        if (parts[1] && parts[1] !== 'all') date = date || parts[1];
      }
      filename = item.photoCount
        ? `Pack completo - ${item.photoCount} fotos`
        : 'Pack completo';
    } else {
      const explicitFilename = (item as any).filename as string | undefined;
      if (explicitFilename) {
        filename = explicitFilename;
      } else if (school && date) {
        const prefix = `${school}_${date}_`;
        if (item.id.startsWith(prefix)) {
          filename = item.id.slice(prefix.length);
        }
      } else {
        const parts = item.id.split('_');
        school = school || parts[0] || '';
        if (parts.length >= 3) date = date || parts[1] || '';
        if (parts.length >= 3) filename = parts.slice(2).join('_');
        else if (parts.length === 2) filename = parts[1] || item.name;
      }
    }

    const out: CheckoutItem = {
      id: item.id,
      filename,
      school,
      date,
      price: item.price,
      thumbnail: item.image,
    };
    if (files?.length) out.files = files;
    return out;
  });
}

const CartModal: React.FC<CartModalProps> = ({
  isOpen,
  onClose,
  items,
  onRemoveItem,
  totalPrice,
  onCheckoutStart,
}) => {
  const { t, language } = useLanguage();
  const { isAuthenticated } = useAuth();
  const { taxRate } = usePricing();
  const [showProfilePrompt, setShowProfilePrompt] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  // Bloqueo síncrono: evita que un doble clic/doble-tap dispare dos sesiones de Stripe
  // antes de que el estado `isProcessing` (asíncrono) desactive el botón.
  const submitLockRef = useRef(false);

  useEffect(() => {
    if (isOpen && items.length > 0 && !isAuthenticated && !wasPromptDismissed('before_pay')) {
      setShowProfilePrompt(true);
    }
  }, [isOpen, items.length, isAuthenticated]);

  if (!isOpen) return null;

  const taxAmount = Math.round(totalPrice * (taxRate / 100) * 100) / 100;
  const orderTotal = Math.round((totalPrice + taxAmount) * 100) / 100;

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleCheckout = async () => {
    if (submitLockRef.current) return;

    if (!customerEmail.trim() || !customerName.trim()) {
      setCheckoutError(t.cart.errorEmailNameRequired);
      return;
    }
    if (!isValidEmail(customerEmail)) {
      setCheckoutError(t.cart.errorInvalidEmail);
      return;
    }
    if (items.length === 0) {
      setCheckoutError(t.cart.errorEmptyCart);
      return;
    }

    submitLockRef.current = true;
    setIsProcessing(true);
    setCheckoutError(null);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const checkoutItems = cartToCheckoutItems(items);

      const token = getAccessToken();
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(`${apiUrl}/stripe/checkout`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          items: checkoutItems,
          customer_email: customerEmail.trim(),
          customer_name: customerName.trim(),
          locale: language,
          analytics_session_id: getAnalyticsSessionId(),
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const detail = typeof data.detail === 'string' ? data.detail : t.cart.errorCheckoutSession;
        throw new Error(detail);
      }

      if (data.url) {
        // Vaciamos el carrito ANTES de salir del sitio: si el cliente vuelve atrás
        // desde Stripe (o desde una pestaña restaurada), no verá el mismo carrito
        // lleno y no intentará pagar de nuevo por la misma compra.
        onCheckoutStart?.();
        window.location.href = data.url;
      } else {
        throw new Error(t.cart.errorNoPaymentUrl);
      }
    } catch (err) {
      submitLockRef.current = false;
      setIsProcessing(false);
      setCheckoutError(err instanceof Error ? err.message : t.cart.errorPaymentFailed);
    }
  };

  const formatPrice = (price: number) => {
    return `€${price.toFixed(2)}`;
  };

  const getItemCount = () => {
    return items.length;
  };

  return (
    <>
      <ProfilePromptModal
        moment="before_pay"
        isOpen={showProfilePrompt}
        onClose={() => setShowProfilePrompt(false)}
      />
    <div className="cart-modal-overlay" onClick={onClose}>
      <div className="cart-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="cart-modal-header">
          <div className="cart-header-title">
            <svg className="cart-header-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="9" cy="21" r="1"></circle>
              <circle cx="20" cy="21" r="1"></circle>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
            </svg>
            <div>
              <h2>{t.cart.title}</h2>
              <p className="cart-subtitle">{getItemCount()} {getItemCount() === 1 ? t.cart.item : t.cart.items}</p>
            </div>
          </div>
          <button className="cart-modal-close" onClick={onClose} aria-label={t.cart.closeCart}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        
        {/* Body */}
        <div className="cart-modal-body">
          {items.length === 0 ? (
            <div className="cart-empty-state">
              <div className="empty-cart-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="9" cy="21" r="1"></circle>
                  <circle cx="20" cy="21" r="1"></circle>
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                </svg>
              </div>
              <h3>{t.cart.empty}</h3>
              <p>{t.cart.emptyDesc}</p>
              <button className="btn-explore" onClick={onClose}>
                {t.cart.explorePhotos}
              </button>
            </div>
          ) : (
            <div className="cart-items-list">
              {items.map((item) => (
                <div key={item.id} className="cart-item-card">
                  <div className="cart-item-details">
                    <h4 className="cart-item-name">{item.name}</h4>
                    <div className="cart-item-meta">
                      {item.photoCount != null && item.photoCount > 0 ? (
                        <span className="cart-item-format">
                          {t.cart.packComplete} · {item.photoCount}{' '}
                          {item.photoCount === 1 ? t.cart.photo : t.cart.photos}
                        </span>
                      ) : (
                        <>
                          <span className="cart-item-format">{t.cart.digitalFormat}</span>
                          <span className="cart-item-quality">{t.cart.highResolution}</span>
                        </>
                      )}
                    </div>
                    <div className="cart-item-price-section">
                      <span className="cart-item-price">{formatPrice(item.price)}</span>
                    </div>
                  </div>

                  {/* Botón eliminar */}
                  <button 
                    className="cart-item-remove"
                    onClick={() => onRemoveItem(item.id)}
                    aria-label={t.cart.removeItem}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      <line x1="10" y1="11" x2="10" y2="17"></line>
                      <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Footer */}
        {items.length > 0 && (
          <div className="cart-modal-footer">
            {/* Datos para checkout Stripe */}
            <div className="cart-checkout-form">
              <input
                type="email"
                placeholder={t.cart.emailPlaceholder}
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                className="cart-input"
                disabled={isProcessing}
                required
              />
              <input
                type="text"
                placeholder={t.cart.namePlaceholder}
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                className="cart-input"
                disabled={isProcessing}
                required
              />
            </div>
            {checkoutError && (
              <p className="cart-checkout-error">{checkoutError}</p>
            )}
            {/* Resumen de precios */}
            <div className="cart-summary">
              <div className="cart-summary-row">
                <span>{t.cart.subtotal}</span>
                <span>{formatPrice(totalPrice)}</span>
              </div>
              <div className="cart-summary-row">
                <span>{t.cart.igic.replace('{rate}', String(taxRate))}</span>
                <span>{formatPrice(taxAmount)}</span>
              </div>
              <div className="cart-summary-row cart-summary-note">
                <span>{t.cart.taxesNotIncludedNote}</span>
              </div>
              <div className="cart-summary-divider"></div>
              <div className="cart-summary-row cart-total">
                <span>{t.cart.total}</span>
                <span className="total-price">{formatPrice(orderTotal)}</span>
              </div>
            </div>

            <div className="cart-before-payment-notice" role="status">
              <svg
                className="cart-before-payment-notice-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              <p className="cart-before-payment-notice-text">
                {t.cart.beforePaymentDownloadNotice}
              </p>
            </div>

            {/* Botones de acción */}
            <div className="cart-actions">
              <button 
                className="btn-checkout"
                onClick={handleCheckout}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <>
                    <span className="btn-spinner"></span>
                    {t.cart.processing}
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                      <line x1="1" y1="10" x2="23" y2="10"></line>
                    </svg>
                    {t.cart.proceedToPayment}
                  </>
                )}
              </button>
              <button className="btn-continue-shopping" onClick={onClose}>
                {t.cart.continueShopping}
              </button>
            </div>

            {/* Información adicional */}
            <div className="cart-info-badges">
              <div className="info-badge">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                </svg>
                <span>{t.cart.securePayment}</span>
              </div>
              <div className="info-badge">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span>{t.cart.instantDownload}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default CartModal;